import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "./apiClient"; // centralized axios instance

// ───── Thunks ─────────────────────────────────────────

// Fetch all Misc Expenses
export const fetchMiscExpenses = createAsyncThunk(
  "miscExpenseData/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/miscExpenseData/");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Insert new Misc Expense (with optional file upload)
export const createMiscExpense = createAsyncThunk(
  "miscExpenseData/create",
  async (formValues, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      if (formValues.tc_id) formData.append("tc_id", formValues.tc_id);
      if (formValues.meTitle) formData.append("meTitle", formValues.meTitle);
      if (formValues.meBillFor) formData.append("meBillFor", formValues.meBillFor);
      if (formValues.meCost) formData.append("meCost", formValues.meCost);
      if (formValues.meDesc) formData.append("meDesc", formValues.meDesc);
      if (formValues.meDate) formData.append("meDate", formValues.meDate);
      if (formValues.meBillFile) formData.append("meBill", formValues.meBillFile);
      formData.append("status", formValues.status || "A");

      const response = await apiClient.post("/miscExpenseData/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update Misc Expense
export const updateMiscExpense = createAsyncThunk(
  "miscExpenseData/update",
  async ({ id, formValues }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      if (formValues.tc_id) formData.append("tc_id", formValues.tc_id);
      if (formValues.meTitle) formData.append("meTitle", formValues.meTitle);
      if (formValues.meBillFor) formData.append("meBillFor", formValues.meBillFor);
      if (formValues.meCost) formData.append("meCost", formValues.meCost);
      if (formValues.meDesc) formData.append("meDesc", formValues.meDesc);
      if (formValues.meDate) formData.append("meDate", formValues.meDate);
      if (formValues.status) formData.append("status", formValues.status);
      if (formValues.meBillFile) formData.append("meBill", formValues.meBillFile);

      const response = await apiClient.put(`/miscExpenseData/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete Misc Expense
export const deleteMiscExpense = createAsyncThunk(
  "miscExpenseData/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(`/miscExpenseData/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ───── Slice ─────────────────────────────────────────
const miscExpenseDataSlice = createSlice({
  name: "miscExpenseData",
  initialState: {
    data: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // FETCH
      .addCase(fetchMiscExpenses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMiscExpenses.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchMiscExpenses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createMiscExpense.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMiscExpense.fulfilled, (state, action) => {
        state.loading = false;
        state.data.push(action.payload);
      })
      .addCase(createMiscExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateMiscExpense.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMiscExpense.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.data.findIndex((item) => item.id === action.payload.id);
        if (idx !== -1) {
          state.data[idx] = action.payload;
        }
      })
      .addCase(updateMiscExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteMiscExpense.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMiscExpense.fulfilled, (state, action) => {
        const { id } = action.payload;
        state.loading = false;
        state.data = state.data.filter((item) => item.id !== id);
      })
      .addCase(deleteMiscExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default miscExpenseDataSlice.reducer;
